import { cloneElement, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn } from 'lucide-react';

/**
 * ChartZoom — click-to-enlarge wrapper for interactive charts (RULES_BLOG_POST click-to-enlarge
 * rule; same portal / Esc / backdrop-click idiom as BlogImage). The child chart is cloned into the
 * lightbox with a taller `height` prop; echarts-for-react remounts cleanly and measures the wider
 * lightbox container.
 *
 * ⚠️ TWO TRAPS THIS COMPONENT FELL INTO, both reported from a real reading of the page:
 *
 *  1. A HARDCODED WHITE PANEL. The lightbox panel was `bg-white` while the charts inside it choose
 *     their label colours from the LIVE theme — so in dark mode a chart drew light-grey labels onto
 *     a white panel and looked broken. The panel now follows the theme (`bg-white dark:bg-slate-900`,
 *     the site's own surface pair). The chart's own opaque background (Rule 956) is the other half
 *     of the fix, so neither side is guessing what the other painted.
 *
 *  2. CLICKING *ANYWHERE* INSIDE OPENED IT. The wrapper had the zoom handler on the whole figure, so
 *     a click on an interactive control inside a chart — the Lane `<select>`, a tab, a button —
 *     bubbled up and threw the lightbox over the page. Zoom is for the FIGURE, so a click that
 *     originated on a control is left alone and the control keeps working.
 */
export const ChartZoom = ({ children }: { children: React.ReactElement }) => {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  /**
   * A click that started on anything interactive belongs to that control, not to the figure.
   * ⚠️ The wrapper ITSELF carries `role="button"`, so a plain `closest()` on an inner control's
   * ancestor chain matches the wrapper and would suppress EVERY click — zoom would silently stop
   * working entirely. The match is therefore only honoured when it is not the wrapper.
   */
  const onFigureClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    const hit = target?.closest(
      'button, a, select, option, input, textarea, label, [role="button"], [role="tab"], [data-no-zoom]',
    );
    if (hit && hit !== e.currentTarget) return;
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, close]);

  return (
    <>
      <div
        className="relative group cursor-zoom-in"
        onClick={onFigureClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
        aria-label="Enlarge chart"
      >
        {children}
        <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <ZoomIn className="w-3.5 h-3.5" /> Enlarge
        </span>
      </div>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label="Enlarged chart"
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-4 right-4 text-white/90 hover:text-white rounded-full p-2 bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
            <div
              className="w-[min(1100px,94vw)] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {cloneElement(children as React.ReactElement<{ height?: number }>, { height: 540 })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
